<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import dayjs from 'dayjs';
import { wizardListenerMixin } from '@/mixins/wizard';
import { DATE_INPUT_FORMAT, DEFAULT_FRACTION_DIGITS } from '@/util/constants';
import VDatePicker from '@/components/v3/patterns/VDatePicker';
import Paper from '@/components/layout/WizardOverviewPaper.vue';
import EzTextarea from '@/components/ui/Textarea/EzTextarea';

/**
 * OverviewSidebar
 * @version 1.0.0
 * @since 3.30.2
 */
export default {
  name: 'OverviewSidebar',
  mixins: [wizardListenerMixin],
  components: {
    VDatePicker,
    Paper,
    EzTextarea,
  },
  data() {
    return {
      drivers: [],
      today: dayjs(new Date()),
      values: {
        transferAt: null,
        note: '',
      },
      editableDeliveryFee: 0,
      DATE_INPUT_FORMAT,
    };
  },
  computed: {
    ...mapState('entities/orders', ['draft']),
    ...mapGetters('entities/users', ['getTax']),
    ...mapGetters('entities/users', ['getDistributor']),
    isTaxUniform() {
      let uniformTax = true;
      this.products.forEach(item => {
        if (item.tax !== this.tax) uniformTax = false;
      });
      return uniformTax;
    },
    venue() {
      return this.draft.venue;
    },
    isTargetAccount() {
      return !this.venue.connection;
    },
    precision() {
      return this.getDistributor.currency?.fractionDigits ?? DEFAULT_FRACTION_DIGITS;
    },
    defaultTransferDate() {
      return this.today;
    },
    products() {
      return this.draft.products;
    },
    draftDeliveryFee() {
      return this.draft?.deliveryFee ?? null;
    },
    deliveryFee() {
      return this.editableDeliveryFee || this.draftDeliveryFee || 0;
    },
    invoices() {
      return this.draft?.invoices || [];
    },
    isSomeProductsTBD() {
      return this.products.some(p => this.isToBeDetermined(p));
    },
    filteredProducts() {
      return this.products.filter(p => !this.isToBeDetermined(p));
    },
    total() {
      return this.products.reduce((a, c) => a + c?.quantity * c?.price || 0, 0);
    },
  },
  methods: {
    ...mapMutations('entities/orders', [
      'SET_DRAFT_DELIVERY',
      'UPDATE_DRAFT',
      'UPDATE_DRAFT_PRODUCT',
    ]),
    editDeliveryFee(val) {
      this.editableDeliveryFee = val;
      this.SET_DRAFT_DELIVERY(this.editableDeliveryFee);
    },
    onNoteUpdate(val) {
      this.values = {
        ...this.values,
        note: val,
      };
      this.UPDATE_DRAFT({ note: val });
    },
    isToBeDetermined(product) {
      const { priceUnit, marketPrice, price } = product || {};
      return (!!priceUnit && price > 0) || marketPrice;
    },
    updateValue(field, val) {
      this.values = { ...this.values, [field]: val };
      this.UPDATE_DRAFT({ [field]: val });
    },
  },
  async created() {
    const { transferAt, note } = this.draft;

    this.values = {
      ...this.values,
      transferAt: this.defaultTransferDate,
      ...(transferAt && { transferAt }),
      note,
    };
  },
};
</script>

<template>
  <div class="create-order__sidebar">
    <paper class="delivery-details">
      <template #title>Transfer Details</template>
      <template>
        <v-date-picker
          name="transferAt"
          label="Transfer Date"
          v-model="values.transferAt"
          @dateChange="updateValue('transferAt', $event)"
          isFullWidth
          class="mb-16"
        />
      </template>
    </paper>
    <paper fullWidth>
      <template #title> Note </template>

      <template>
        <ez-textarea
          formKey=""
          placeholder="Add a note for this transfer."
          name="note"
          class="mt-16"
          :value="values.note"
          @onChange="onNoteUpdate"
        />
      </template>
    </paper>
  </div>
</template>

<style lang="scss" scoped>
.create-order {
  &__sidebar {
    flex: 1 0 315px;
    margin-left: $spacing-16;

    @media (max-width: 768px) {
      flex: 0 0 auto;
      margin-left: 0;
      width: 100%;
    }

    .delivery-details {
      :deep() .date-picker {
        width: 100%;
      }
      .driver-dropdown {
        :deep() .select-search {
          &__trigger span {
            @include font-size(14px, 20px);

            &:not(.select-search__value) {
              color: $color-gray-6C;
            }
          }

          &__search-wrapper {
            display: none;
          }

          &__item {
            @include font-size(14px, 20px);
            padding: 10px 16px;
            color: $color-gray-25;
          }
        }
      }

      .delivery-fee :deep() {
        .label {
          font-weight: 500;
        }

        input {
          font-weight: 400;
        }

        .mark-input__error {
          display: none;
        }
      }

      @media (max-width: 768px) {
        &.overview-paper {
          margin: 0;
          margin-top: 12px;
          width: 100%;
          max-width: 100%;
        }
      }
    }

    .custom-documents {
      .invoice-upload {
        :deep() .preview__images {
          flex-direction: column;
          .preview__image {
            & + .preview__image {
              margin-left: 0;
            }
            .image-name {
              font-size: 14px;
              margin-right: 12px;
            }
          }
        }
      }
    }

    .discount {
      &__label {
        display: block;
        color: $color-gray-6C;
        @include font-size(14px, 22px);
      }

      &__input :deep() {
        width: auto;

        .mask-input {
          &__input {
            @include font-size(16px, 20px);
          }

          &__prefix {
            color: $color-gray-6C;
            font-weight: bold;
          }
        }

        .mark-input__error {
          display: none;
        }
      }
    }

    .order-totals {
      &__label {
        @include font-size(14px, 20px);
        color: $color-gray-6C;

        &--total {
          @include font-size(16px, 22px);
          font-weight: bold;
        }
      }

      &__amount {
        @include font-size(16px, 22px);
        color: $color-gray-25;
        font-weight: bold;
      }
    }
  }
}
</style>
